import React, { useMemo } from 'react';
import styled from 'styled-components';
import theme from '../../../styles/theme';
import Card from '../../../components/card/Card';
import Button from '../../../components/button/Button';
import H from '../../../components/texts/h/H';
import P from '../../../components/texts/p/P';
import Span from '../../../components/texts/span/Span';
import Input from '../../../components/input/Input';
import Icon from '../../../components/icon/Icon';
import DropDown from '../../../components/dropdown/DropDown';
import Table from '../../../components/table/table';
import { MontoAOtorgarProps, ProductoOtorgado } from './CreditoMaximo.types';
import { useClientTheme } from '../../../context/ThemeContext';
import { useConfig } from '../../../context/ConfigContext';
import { formatCurrency } from '../../../utils/Masks';
import TruncatedTextWithTooltip from '../../../components/TruncatedTextWithTooltip/TruncatedTextWithTooltip';
import {
  buildActivoMontoOtorgadoDropdownOptions,
  formatActivoMontoOtorgadoLabel,
  isProductoSinActivoMontoOtorgado,
  resolveUnitSaleForProduct,
} from '../../../utils/creditoMaximoMonto';
import {
  ACTIVO_SIN_COTIZACION_LABEL,
  buildOriginationCurrencyDropdownItems,
  resolveLocalCurrencyCode,
  USD_HISTORIC_LOCK_BANNER_TEXT,
} from '../../../utils/quotationAssets';
import {
  getManagementAssetRules,
  getMissingQuotationMessages,
  isAssetOptionEnabled,
} from '../../../utils/managementAssetRules';
import {
  productLineDropdownTitle,
  resolveSelectableProductLineLabel,
  UNAVAILABLE_PRODUCT_LINE_LABEL,
} from '../../../utils/productLines';
import Tooltip from '../../../components/tooltip/Tooltip';
import { CURRENCY_CODES } from '../../../utils/Constants';

const TOLERANCE = 0.01;

const getGridColumns = (showAssets: boolean, showActions: boolean): string => {
  const cols: string[] = ['4fr'];
  if (showAssets) {
    cols.push('1.5fr', '1fr');
  }
  cols.push('1fr', '3fr');
  if (showActions) {
    cols.push('40px');
  }
  return cols.join(' ');
};

const TableGrid = styled.div<{ $columns: string }>`
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns};
  column-gap: 8px;
  row-gap: 8px;
  align-items: center;
`;

const HeaderCell = styled.div`
  padding: 4px 0 6px;
`;

const Cell = styled.div`
  display: flex;
  align-items: center;
  min-height: 40px;

  /* Todos los hijos directos llenan el ancho */
  > * {
    margin: 0;
    width: 100%;
    min-width: 0;
  }

  /* DropDown wrapper interno */
  > div > div {
    margin-top: 0;
  }

  input {
    width: 100%;
    box-sizing: border-box;
  }
`;

const ActionCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid ${theme.colors.danger.border.regular};
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${theme.colors.danger.bg.weak || '#fbe9e7'};
  }
`;

const AlertRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 8px;
`;

const InlineWrapper = styled.div`
  display: flex;
  gap: 20px;
  align-items: stretch;
  justify-content: space-between;
  width: 100%;
`;

const sumByCurrency = (items: ProductoOtorgado[], code: string): number =>
  items
    .filter((p) => (p.currency_code || '').toUpperCase() === code.toUpperCase())
    .reduce((sum, p) => sum + (parseFloat(p.monto_otorgado) || 0), 0);

const isMontoInconsistent = (
  producto: ProductoOtorgado,
  assets: Array<{ code: string; name: string; saleValue?: number | string }>
): boolean => {
  if (isProductoSinActivoMontoOtorgado(producto)) return false;
  const unit = resolveUnitSaleForProduct(producto, assets);
  if (unit == null) return false;
  const cantidad = parseInt(producto.cantidad_otorgada, 10) || 0;
  if (cantidad <= 0) return false;
  const expected = unit * cantidad;
  const actual = parseFloat(producto.monto_otorgado) || 0;
  return Math.abs(expected - actual) > TOLERANCE;
};

const MontoAOtorgar: React.FC<MontoAOtorgarProps> = ({
  productos,
  productLines,
  superaMaximo,
  onProductoChange,
  onDeleteProducto,
  onAddProducto,
  onBlur,
  readOnly = false,
  isQuotationsEnabled = true,
  availableAssets = [],
  summaryLayout = 'below',
  usdAvailable = true,
  isUsdHistoricLocked = false,
  managementCode = null,
  localCurrencyCode: localCurrencyCodeProp,
}) => {
  const { clientTheme } = useClientTheme();
  const config = useConfig();
  const localCurrencyCode =
    localCurrencyCodeProp ?? resolveLocalCurrencyCode(config);
  const managementRules = useMemo(
    () => getManagementAssetRules(managementCode),
    [managementCode]
  );

  const resolveProductLabel = (code: string): string =>
    resolveSelectableProductLineLabel(productLines, code) ?? UNAVAILABLE_PRODUCT_LINE_LABEL;

  const currencyOptions = useMemo(
    () =>
      buildOriginationCurrencyDropdownItems(
        localCurrencyCode,
        CURRENCY_CODES.usd,
        usdAvailable
      ),
    [localCurrencyCode, usdAvailable]
  );
  const showAssetColumns = isQuotationsEnabled;
  const showActions = !readOnly && !isUsdHistoricLocked && !!onDeleteProducto;
  const gridColumns = getGridColumns(showAssetColumns, showActions);
  const totalLocal = sumByCurrency(productos, localCurrencyCode);
  const totalUsd = sumByCurrency(productos, CURRENCY_CODES.usd);

  const activoDropdownAssets = useMemo(
    () =>
      showAssetColumns
        ? buildActivoMontoOtorgadoDropdownOptions(
            availableAssets,
            managementRules?.assetCodes ?? null
          )
        : [],
    [showAssetColumns, availableAssets, managementRules]
  );

  const quotationWarningMessages = useMemo(() => {
    if (!showAssetColumns || !managementRules) return [] as string[];
    const msgs = productos.flatMap((p) =>
      getMissingQuotationMessages({
        rules: managementRules,
        assetCode: p.asset_code,
        currencyCode: p.currency_code,
        assets: availableAssets,
      })
    );
    return [...new Set(msgs)];
  }, [showAssetColumns, managementRules, productos, availableAssets]);

  const hasBannerAboveContent =
    isUsdHistoricLocked || quotationWarningMessages.length > 0 || !readOnly;

  const handleProductLineChange = (productId: number, label: string) => {
    const match = productLines.find((pl) => pl.label === label);
    if (match) onProductoChange(productId, 'product_line_code', match.code);
  };

  const handleAssetChange = (productId: number, name: string) => {
    if (name === ACTIVO_SIN_COTIZACION_LABEL) {
      onProductoChange(productId, 'asset_code', '');
      onProductoChange(productId, 'asset_name', '');
      onProductoChange(productId, 'cantidad_otorgada', '0');
      return;
    }
    const match = activoDropdownAssets.find((a) => a.name === name);
    if (match && match.code !== '') {
      onProductoChange(productId, 'asset_code', match.code);
      onProductoChange(productId, 'asset_name', match.name);
    }
  };

  const handleCurrencyChange = (productId: number, label: string) => {
    onProductoChange(productId, 'currency_code', label);
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      <Card variant={clientTheme ? 'primary' : 'success'} modifier='weak' spacing={'2'}>
        <H type='4' variant='neutral-1' withMargin={false} weight='bold' modifier='strong'>
          Monto a otorgar
        </H>
      </Card>
      {isUsdHistoricLocked && (
        <Card
          display='flex'
          margin={{ top: '3', bottom: '2' }}
          spacing='3'
          variant='warning'
          modifier='weak'
          gap='8px'
          borderRadiusAmount='4'
        >
          <Icon name='warning_triangle_outline' variant='warning' width={24} height={24} />
          <P type='2' variant='neutral' withMargin={false}>
            {USD_HISTORIC_LOCK_BANNER_TEXT}
          </P>
        </Card>
      )}
      {quotationWarningMessages.length > 0 && (
        <Card
          display='flex'
          margin={{ top: '3', bottom: '2' }}
          spacing='3'
          variant='warning'
          modifier='weak'
          gap='8px'
          borderRadiusAmount='4'
          style={{ flexDirection: 'column' }}
        >
          {quotationWarningMessages.map((msg) => (
            <Card key={msg} display='flex' gap='8px' alignItems='center'>
              <Icon name='warning_triangle_outline' variant='warning' width={24} height={24} />
              <P type='2' variant='neutral' withMargin={false}>
                {msg}
              </P>
            </Card>
          ))}
        </Card>
      )}
      {!readOnly && (
        <Card spacing={'2'} margin={{ top: '3', bottom: '2' }}>
          <P type='2' variant='neutral' withMargin={false}>
            {isUsdHistoricLocked
              ? 'Los productos en USD están en solo lectura. Podés guardar sin modificar los montos.'
              : 'Modificá la solicitud en caso de ser necesario.'}
          </P>
        </Card>
      )}
      <Card margin={hasBannerAboveContent ? undefined : { top: '4' }} width='100%'>
      <div style={{ padding: '0 8px' }}>
        {(() => {
          const isInline = summaryLayout === 'inline' && readOnly;

          const readOnlyColumns = [
            {
              name: 'Producto',
              selector: (row: ProductoOtorgado) => (
                <TruncatedTextWithTooltip text={resolveProductLabel(row.product_line_code)}>
                  <Span type='3' variant='neutral'>
                    {resolveProductLabel(row.product_line_code)}
                  </Span>
                </TruncatedTextWithTooltip>
              ),
              grow: 1,
            },
            ...(showAssetColumns
              ? [
                  {
                    name: 'Activo',
                    selector: (row: ProductoOtorgado) => (
                      <TruncatedTextWithTooltip text={formatActivoMontoOtorgadoLabel(row)}>
                        <Span type='3' variant='neutral'>{formatActivoMontoOtorgadoLabel(row)}</Span>
                      </TruncatedTextWithTooltip>
                    ),
                    grow: 1,
                  },
                  {
                    name: 'Cantidad',
                    selector: (row: ProductoOtorgado) => (
                      <Span type='3' variant='neutral'>
                        {row.cantidad_otorgada || '-'}
                      </Span>
                    ),
                    grow: 0.5,
                  },
                ]
              : []),
            {
              name: 'Moneda',
              selector: (row: ProductoOtorgado) => (
                <Span type='3' variant='neutral'>{row.currency_code}</Span>
              ),
              grow: 1,
            },
            {
              name: 'Monto',
              selector: (row: ProductoOtorgado) => {
                const inconsistent = isMontoInconsistent(row, availableAssets);
                const unit = inconsistent ? resolveUnitSaleForProduct(row, availableAssets) : null;
                const cant = parseInt(row.cantidad_otorgada, 10) || 0;
                const expectedAmount =
                  unit != null && cant > 0 ? unit * cant : null;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                    {inconsistent && expectedAmount != null && (
                      <Tooltip
                        msg={`Esperado: ${formatCurrency(expectedAmount, true)} (${row.cantidad_otorgada} × cotización)`}
                        position='top'
                      >
                        <Icon name='warning_triangle_outline' variant='warning' modifier='strong' height={14} width={14} />
                      </Tooltip>
                    )}
                    <Span type='3' variant={inconsistent ? 'warning' : 'neutral'}>
                      {formatCurrency(parseFloat(row.monto_otorgado) || 0, true)}
                    </Span>
                  </div>
                );
              },
              grow: 1,
              right: true,
            },
          ];

          const tableContent = readOnly ? (
            <Table
              columns={readOnlyColumns}
              data={productos}
              tableData={{ size: productos.length, totalElements: productos.length }}
              variant='weak'
              disablePagination
            />
          ) : (
            <TableGrid $columns={gridColumns}>
              <HeaderCell>
                <Span type='4' variant='neutral' weight='bold'>Producto</Span>
              </HeaderCell>
              {showAssetColumns && (
                <>
                  <HeaderCell>
                    <Span type='4' variant='neutral' weight='bold'>Activo</Span>
                  </HeaderCell>
                  <HeaderCell>
                    <Span type='4' variant='neutral' weight='bold'>Cantidad</Span>
                  </HeaderCell>
                </>
              )}
              <HeaderCell>
                <Span type='4' variant='neutral' weight='bold'>Moneda</Span>
              </HeaderCell>
              <HeaderCell>
                <Span type='4' variant='neutral' weight='bold'>Monto</Span>
              </HeaderCell>
              {showActions && <HeaderCell />}

              {productos.map((producto, rowIndex) => {
                const assetLabel = formatActivoMontoOtorgadoLabel(producto);
                const hasAsset = !isProductoSinActivoMontoOtorgado(producto);

                return (
                  <React.Fragment key={`monto-otorgar-${producto.product_id}-${rowIndex}`}>
                    <Cell>
                      <DropDown
                        id={`producto-${producto.product_id}`}
                        title={productLineDropdownTitle(
                          productLines,
                          producto.product_line_code
                        )}
                        items={productLines.map((pl) => ({ label: pl.label }))}
                        size='sm'
                        onChange={(label) => handleProductLineChange(producto.product_id, label)}
                        disabled={isUsdHistoricLocked}
                      />
                    </Cell>

                    {showAssetColumns && (
                      <>
                        <Cell>
                          {activoDropdownAssets.length > 0 ? (
                            <DropDown
                              id={`activo-${producto.product_id}`}
                              title={assetLabel}
                              items={activoDropdownAssets.map((a) => ({
                                label: a.name,
                                disabled: managementRules
                                  ? !isAssetOptionEnabled(
                                      managementRules,
                                      a.code,
                                      availableAssets
                                    )
                                  : false,
                              }))}
                              size='sm'
                              onChange={(name) => handleAssetChange(producto.product_id, name)}
                              disabled={isUsdHistoricLocked}
                            />
                          ) : (
                            <Span type='3' variant='neutral'>{assetLabel}</Span>
                          )}
                        </Cell>
                        <Cell>
                          {hasAsset ? (
                            <Input
                              id={`cantidad-${producto.product_id}`}
                              value={producto.cantidad_otorgada}
                              placeholder='0'
                              onlyNumbers
                              size='md'
                              disabled={isUsdHistoricLocked}
                              onChange={(e) =>
                                onProductoChange(producto.product_id, 'cantidad_otorgada', e.target.value)
                              }
                              onBlur={onBlur}
                            />
                          ) : (
                            <Span type='3' variant='neutral'>-</Span>
                          )}
                        </Cell>
                      </>
                    )}

                    <Cell>
                      <DropDown
                        id={`moneda-${producto.product_id}`}
                        title={producto.currency_code}
                        items={currencyOptions}
                        size='sm'
                        onChange={(label) => handleCurrencyChange(producto.product_id, label)}
                        disabled={isUsdHistoricLocked}
                      />
                    </Cell>

                    <Cell>
                      <Input
                        id={`monto-${producto.product_id}`}
                        value={producto.monto_otorgado}
                        placeholder='0'
                        onlyNumbers
                        size='md'
                        readOnly={!!producto.asset_code || isUsdHistoricLocked}
                        disabled={isUsdHistoricLocked && !producto.asset_code}
                        onChange={(e) =>
                          onProductoChange(producto.product_id, 'monto_otorgado', e.target.value)
                        }
                        onBlur={onBlur}
                      />
                    </Cell>

                    {showActions && (
                      <ActionCell>
                        <DeleteButton
                          type='button'
                          aria-label='Eliminar producto'
                          onClick={() => onDeleteProducto!(producto.product_id)}
                        >
                          <Icon
                            name='delete_outline'
                            variant='danger'
                            height={18}
                            width={18}
                          />
                        </DeleteButton>
                      </ActionCell>
                    )}
                  </React.Fragment>
                );
              })}
            </TableGrid>
          );

          const themeVariant = clientTheme ? 'primary' : 'success';
          const summaryContent = (
            <Card
              border={true}
              variant={themeVariant}
              modifier='weak'
              spacing='1'
              display='flex'
              flexDirection='column'
              justifyContent='center'
              alignItems='center'
              gap='8px'
              width='100%'
              height='100%'
            >
              <H type='2' withMargin={false} weight='bold' variant={themeVariant} align='center'>
                {localCurrencyCode} {formatCurrency(totalLocal, true)}
              </H>
              <H type='2' withMargin={false} weight='bold' variant={themeVariant} align='center'>
                {CURRENCY_CODES.usd} {formatCurrency(totalUsd, true)}
              </H>
            </Card>
          );

          return isInline ? (
            <InlineWrapper>
              <div style={{ flex: 1, minWidth: 0 }}>{tableContent}</div>
              <div style={{ flex: 1, minWidth: 0, display: 'flex' }}>
                {summaryContent}
              </div>
            </InlineWrapper>
          ) : (
            <>
              {tableContent}
              {readOnly && (
                <Card margin={{ top: '3' }} display='flex' flexDirection='column' gap='8px'>
                  <Card display='flex' gap='8px' flexWrap='wrap'>
                    <P type='2' variant='neutral' weight='bold' withMargin={false}>
                      Total {localCurrencyCode}
                    </P>
                    <P type='2' variant='neutral' withMargin={false}>
                      {formatCurrency(totalLocal, true)}
                    </P>
                  </Card>
                  <Card display='flex' gap='8px' flexWrap='wrap'>
                    <P type='2' variant='neutral' weight='bold' withMargin={false}>
                      Total {CURRENCY_CODES.usd}
                    </P>
                    <P type='2' variant='neutral' withMargin={false}>
                      {formatCurrency(totalUsd, true)}
                    </P>
                  </Card>
                </Card>
              )}
            </>
          );
        })()}

        {!readOnly && !isUsdHistoricLocked && onAddProducto && (
          <Card margin={{ top: '2' }} display='flex'>
            <Button
              id='monto-a-otorgar-agregar-producto'
              variant='tertiary'
              size='md'
              type='button'
              onClick={() => void onAddProducto()}
            >
              + Agregar producto
            </Button>
          </Card>
        )}

        {superaMaximo && (
          <AlertRow>
            <Icon name='warning_triangle_outline' variant='warning' modifier='strong' />
            <Span type='3' variant='warning' modifier='strong'>
              El total otorgado supera el crédito máximo
            </Span>
          </AlertRow>
        )}

        {availableAssets.length > 0 && productos.some((p) => isMontoInconsistent(p, availableAssets)) && (
          <AlertRow>
            <Icon name='warning_triangle_outline' variant='warning' modifier='strong' />
            <Span type='3' variant='warning' modifier='strong'>
              Hay productos con monto inconsistente respecto a la cotización vigente
            </Span>
          </AlertRow>
        )}
      </div>
      </Card>
    </div>
  );
};

export default MontoAOtorgar;
